import React, { useContext, useMemo, useState, useCallback } from 'react';
import moment from 'moment';
import styled, { css } from 'styled-components';
import { Tooltip, TextWithTooltip, ContextMenu as BaseContextMenu, FlexMiddle } from '@ubeya/shared-web/components';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useTranslatedOptions from '@ubeya/shared/hooks/useTranslatedOptions';
import privileges from '@ubeya/shared-web/privileges';
import { usePermissionGate } from '@ubeya/shared/contexts/PermissionGateContext';
import ProjectDeleteFormModal from 'scheduling/components/ManageBooking/ProjectDeleteFormModal';

const ContextMenu = styled(BaseContextMenu)`
  color: ${({ theme }) => theme.colors.black};
  display: none;
  flex-shrink: 0;
  z-index: 5;

  .circle-icon {
    padding: 1px;
    height: 20px;
    width: 20px;
    background: ${({ theme }) => theme.colors.white};
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const PlannerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $totalDays }) => $totalDays}, 1fr);
  width: 100%;
  height: 28px;
`;

const PlannerName = styled(TextWithTooltip)`
  position: absolute;
  inset-inline-start: 8px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding-inline-start: 4px;
  padding-inline-end: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 400;
  font-size: 12px;
  max-width: calc((100% - 16px) / ${({ $spanDays }) => $spanDays} - 40px);
  z-index: 1;
`;

const DayStatsCellContent = styled(FlexMiddle)`
  width: 100%;
  height: 100%;
  justify-content: flex-end;
`;

const DayStatsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding-inline-end: 4px;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.grey1};

  ${({ $isFirst }) =>
    $isFirst &&
    css`
      margin-inline-start: 8px;
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    `}
  ${({ $isLast }) =>
    $isLast &&
    css`
      margin-inline-end: 8px;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    `}
`;

const PlannerBar = styled.div`
  grid-column: ${({ $startCol }) => $startCol} / ${({ $endCol }) => $endCol};
  position: relative;
  display: grid;
  grid-template-columns: repeat(${({ $spanDays }) => $spanDays}, 1fr);
  align-items: center;

  color: ${({ theme }) => theme.colors.black};
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  overflow: visible;

  &:hover {
    ${DayStatsCell} {
      background-color: ${({ theme }) => theme.colors.grey2};
    }

    ${ContextMenu} {
      display: block;
    }
  }
`;

const getStatsForDate = (project, date) => {
  const dateStr = moment(date).format(API_DATE_FORMAT);
  const dateData = project?.dates?.[dateStr];
  const stats = dateData?.stats || {};

  return {
    confirmed: stats.confirmed || 0,
    booked: stats.booked || 0,
    needed: stats.needed || 0,
    clockedIn: stats.clockedIn || 0
  };
};

const formatStats = (stats, accountId) => {
  const { confirmed, booked, needed, clockedIn } = stats;
  if (accountId === 179) {
    return `${clockedIn}/${confirmed}/${booked}${needed > 0 ? `/${needed}` : ''}`;
  }
  return `${confirmed}/${booked}${needed > 0 ? `/${needed}` : ''}`;
};

const PlannerRowItem = ({
  id,
  displayName,
  startCol,
  endCol,
  spanDays,
  daysInSpan,
  project,
  weekStartDate,
  weekEndDate,
  totalDays,
  onClick
}) => {
  const { t } = useContext(ConfigContext);
  const { contextMenuPermissionGate } = usePermissionGate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const onClickDelete = useCallback(() => setIsDeleteModalOpen(true), []);
  const onCloseDeleteModal = useCallback(() => setIsDeleteModalOpen(false), []);

  const contextMenuOptions = useTranslatedOptions(
    [
      {
        label: 'delete',
        onClick: onClickDelete,
        actionRequiredPrivilege: [privileges.DELETE_EVENTS],
        readRequiredPrivilege: [privileges.DELETE_EVENTS]
      }
    ].map((item) => contextMenuPermissionGate(item))
  );

  const handleBarClick = useCallback(() => {
    onClick?.({ project, weekStartDate, weekEndDate });
  }, [onClick, project, weekStartDate, weekEndDate]);

  const stopPropagation = useCallback((e) => e.stopPropagation(), []);

  const showContextMenu = contextMenuOptions.length > 0 && contextMenuOptions.some((option) => option.visible);
  return (
    <>
      <PlannerGrid $totalDays={totalDays}>
        <PlannerBar $startCol={startCol} $endCol={endCol} $spanDays={spanDays} onClick={handleBarClick}>
          <PlannerName $spanDays={spanDays}>{displayName}</PlannerName>
          {daysInSpan.map(({ date, stats, formattedStats }, index) => {
            const isLast = index === daysInSpan.length - 1;
            const isFirst = index === 0;

            return (
              <DayStatsCell $isFirst={isFirst} $isLast={isLast} key={date.format(API_DATE_FORMAT)}>
                <Tooltip
                  body={t('shiftsIndicatorTooltip', {
                    confirmed: stats.confirmed,
                    clockedIn: stats.clockedIn,
                    booked: stats.booked,
                    needed: stats.needed
                  })}
                  placement="bottom">
                  <DayStatsCellContent>{formattedStats}</DayStatsCellContent>
                </Tooltip>
                {isLast && showContextMenu && (
                  <ContextMenu options={contextMenuOptions} onClick={stopPropagation} usePortal />
                )}
              </DayStatsCell>
            );
          })}
        </PlannerBar>
      </PlannerGrid>
      {isDeleteModalOpen && <ProjectDeleteFormModal projectIds={[id]} onClose={onCloseDeleteModal} />}
    </>
  );
};

const WeeklyPlannerBar = ({ weeklyPlannerProjects = [], onClick, weekDays = [] }) => {
  const { t } = useContext(ConfigContext);
  const { mappedLocations } = useLocations();
  const { accountId } = useAccount();

  // Always use 7 columns to match the calendar grid
  const totalDays = weekDays.length;

  // Find the first and last valid days for week boundaries
  const validWeekDays = useMemo(() => weekDays.filter(Boolean), [weekDays]);

  // Create a map of date string to column index (1-indexed for CSS grid)
  const dateToColumnMap = useMemo(() => {
    const map = {};
    weekDays.forEach((day, index) => {
      if (day) {
        const dateStr = moment(day).format(API_DATE_FORMAT);
        map[dateStr] = index + 1; // CSS grid is 1-indexed
      }
    });
    return map;
  }, [weekDays]);

  const plannerLines = useMemo(() => {
    if (validWeekDays.length === 0) return [];

    const weekStartMoment = moment(validWeekDays[0]);
    const weekEndMoment = moment(validWeekDays[validWeekDays.length - 1]);

    return weeklyPlannerProjects.map((project) => {
      const { id, name, locationId, date: projectStart, endDate: projectEnd } = project;

      // Get display name
      const displayName = name || (locationId && mappedLocations[locationId]?.name) || t('weeklyPlanner');

      // Calculate project span within the week
      const projectStartMoment = moment(projectStart);
      const projectEndMoment = moment(projectEnd || projectStart);

      // Clamp to week boundaries (only valid days)
      const effectiveStart = moment.max(projectStartMoment, weekStartMoment);
      const effectiveEnd = moment.min(projectEndMoment, weekEndMoment);

      // Get column positions from the date-to-column map
      const startCol = dateToColumnMap[effectiveStart.format(API_DATE_FORMAT)] || 1;
      const endCol = (dateToColumnMap[effectiveEnd.format(API_DATE_FORMAT)] || startCol) + 1; // +1 because grid-column end is exclusive
      const spanDays = endCol - startCol;

      // Get stats for each day in the span (only for valid days that have data)
      const daysInSpan = [];
      for (let i = 0; i < spanDays; i++) {
        const dayMoment = moment(effectiveStart).add(i, 'days');
        const stats = getStatsForDate(project, dayMoment);
        daysInSpan.push({
          date: dayMoment,
          stats,
          formattedStats: formatStats(stats, accountId)
        });
      }

      return {
        id,
        displayName,
        startCol,
        endCol,
        spanDays,
        daysInSpan,
        project,
        weekStartDate: validWeekDays[0],
        weekEndDate: validWeekDays[validWeekDays.length - 1]
      };
    });
  }, [weeklyPlannerProjects, validWeekDays, dateToColumnMap, mappedLocations, t, accountId]);

  if (plannerLines.length === 0 || totalDays === 0) {
    return null;
  }

  return (
    <Wrapper>
      {plannerLines.map((plannerLine) => (
        <PlannerRowItem key={plannerLine.id} {...plannerLine} totalDays={totalDays} onClick={onClick} />
      ))}
    </Wrapper>
  );
};

export default React.memo(WeeklyPlannerBar);
